import React, {type FC, type ReactNode, useRef} from 'react';
import {useHover} from '@react-aria/interactions';
import {OverlayContainer, type Placement, useOverlayPosition, useOverlayTrigger} from '@react-aria/overlays';
import {useOverlayTriggerState} from '@react-stately/overlays';

import {useEventListener} from 'usehooks-ts';

import styles from './styles.module.css';

interface HoverPopoverProps {
    children: ReactNode;
    OverlayComponent: ReactNode;
    showDelay?: number;
    hideDelay?: number;
    placement?: Placement;
    offset?: number;
    positionTargetRef?: React.RefObject<HTMLElement>;
    isHoverDisabled?: boolean;
    onOpen?: (event: MouseEvent) => void;
    onClose?: (event: MouseEvent) => void;
}

export const HoverPopover: FC<HoverPopoverProps> = ({
    children,
    OverlayComponent,
    showDelay = 500,
    hideDelay = 500,
    placement = 'bottom',
    offset = 10,
    positionTargetRef = null,
    isHoverDisabled = false,
    onOpen,
    onClose,
}) => {
    const state = useOverlayTriggerState({});
    const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);

    const handleHoverStart = () => {
        if (hoverTimeout.current) {
            clearTimeout(hoverTimeout.current);
        }
        hoverTimeout.current = setTimeout(() => state.open(), showDelay);
    };

    const handleHoverEnd = () => {
        if (hoverTimeout.current) {
            clearTimeout(hoverTimeout.current);
        }
        hoverTimeout.current = setTimeout(() => state.close(), hideDelay);
    };

    const {hoverProps: triggerHoverProps} = useHover({
        onHoverStart: handleHoverStart,
        onHoverEnd: handleHoverEnd,
        isDisabled: isHoverDisabled,
    });

    const {hoverProps: overlayHoverProps} = useHover({
        onHoverStart: handleHoverStart,
        onHoverEnd: handleHoverEnd,
        isDisabled: !state.isOpen || isHoverDisabled,
    });

    const togglePopover = (event: React.MouseEvent) => {
        if (hoverTimeout.current) {
            clearTimeout(hoverTimeout.current);
        }

        if (state.isOpen) {
            onClose?.(event.nativeEvent);
        } else {
            onOpen?.(event.nativeEvent);
        }

        state.toggle();
    };

    const {triggerProps, overlayProps} = useOverlayTrigger({type: 'menu'}, state, overlayRef);

    const {overlayProps: positionProps} = useOverlayPosition({
        targetRef: positionTargetRef || triggerRef,
        overlayRef,
        isOpen: state.isOpen,
        placement,
        offset,
    });

    const handleClickOutside = (event: MouseEvent) => {
        if (triggerRef.current &&
            overlayRef.current &&
            !triggerRef.current.contains(event.target as Node) &&
            !overlayRef.current.contains(event.target as Node)
        ) {
            if (hoverTimeout.current) {
                clearTimeout(hoverTimeout.current);
            }
            state.close();
            onClose?.(event);
        }
    };

    useEventListener('mousedown', handleClickOutside);

    return (
        <div>
            <div
                {...triggerProps}
                {...triggerHoverProps}
                ref={triggerRef}
                onClickCapture={togglePopover}
            >
                {children}
            </div>
            {state.isOpen &&
                <OverlayContainer>
                    <div
                        {...overlayProps}
                        {...positionProps}
                        ref={overlayRef}
                        {...overlayHoverProps}
                        className={styles.overlay}
                        onClickCapture={(e) => setTimeout(() => togglePopover(e))}
                    >
                        {OverlayComponent}
                    </div>
                </OverlayContainer>
            }
        </div>
    );
};
